// Advanced search: a form that writes the query string; the preview teaches the syntax, and the button
// says how many records it finds. With ?feed=ID it edits the search behind a feed instead: the records the
// feed keeps must match it, and a preview says what a change would add and take away before it's saved.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api, errorText } from "../api/client";
import type { Feed } from "../api/types";
import { useUI } from "../app/context";
import { keys, useFeeds } from "../app/queries";
import { Modal } from "../components/Modal";
import {
  ADV_FACETS,
  composeQuery,
  parseForForm,
  remember,
  searchPath,
  type AdvancedForm,
  type OptionValue,
  type SearchOptions,
  type SearchResponse,
} from "../api/search";
import { GroupPicker } from "../components/GroupPicker";
import { useWidePage } from "./Search";

// Long lists get a searchable box; short ones stay checkboxes. Either way, nothing chosen means any.
const PICKED: Record<string, { placeholder: string; label: string }> = {
  topic: { placeholder: "Any topic: type to narrow, e.g. training, autonomy", label: "Topics" },
  source: { placeholder: "Any source: type to narrow, e.g. SAM, DIU, Navy", label: "Sources" },
};

export function Advanced() {
  const [params] = useSearchParams();
  const feedId = Number(params.get("feed")) || null;
  const feeds = useFeeds(!!feedId);
  const feed = feedId ? (feeds.data?.feeds.find((x) => x.id === feedId) ?? null) : null;
  const q = feed ? feed.query : (params.get("q") ?? "");
  useWidePage();
  const options = useQuery({
    queryKey: ["search", "options"],
    queryFn: () => api<SearchOptions>("/api/search/options"),
    staleTime: 10 * 60_000,
  });
  if (!options.data || (feedId && !feeds.data)) return <p className="empty">Loading…</p>;
  if (feedId && !feed?.editable) return <p className="empty">That feed isn’t one you can change.</p>;
  return <AdvancedPage key={`${feedId}:${q}`} q={q} options={options.data} feed={feed} />;
}

interface Preview {
  before: Record<"open" | "upcoming" | "ending", number>;
  after: Record<"open" | "upcoming" | "ending", number>;
  added: number;
  removed: number;
  add_examples: { key: string; title: string; buyer: string }[];
  remove_examples: { key: string; title: string; buyer: string }[];
}

function AdvancedPage({ q, options, feed }: { q: string; options: SearchOptions; feed: Feed | null }) {
  const [f, setF] = useState<AdvancedForm>(() => parseForForm(q));
  const [count, setCount] = useState<number | null>(null);
  const [preview, setPreview] = useState<Preview | "loading" | null>(null);
  const [naming, setNaming] = useState(false);
  const navigate = useNavigate();
  const client = useQueryClient();
  const { showToast } = useUI();
  const query = useMemo(() => composeQuery(f), [f]);
  // A feed keeps its own views of open, upcoming and closed records: status and sole-source don't apply.
  const feedQuery = useMemo(() => composeQuery({ ...f, status: "open", sole: false }), [f]);
  const set = <K extends keyof AdvancedForm>(k: K, v: AdvancedForm[K]) => setF((x) => ({ ...x, [k]: v }));

  useEffect(() => setPreview(null), [feedQuery]);
  const runPreview = async () => {
    setPreview("loading");
    try {
      setPreview(await api<Preview>(`/api/feeds/${feed!.id}/preview`, { method: "POST", body: { query: feedQuery } }));
    } catch (err) {
      setPreview(null);
      showToast(errorText(err));
    }
  };
  const saveToFeed = async () => {
    try {
      await api(`/api/feeds/${feed!.id}`, { method: "PATCH", body: { query: feedQuery } });
      await client.invalidateQueries({ queryKey: keys.feeds });
      await client.invalidateQueries({ queryKey: ["feed"] });
      navigate(`/feeds/${feed!.id}`);
      showToast(`Saved. ${feed!.name} now keeps only what matches its search.`);
    } catch (err) {
      showToast(errorText(err));
    }
  };
  const saveAsFeed = async (name: string) => {
    try {
      // From a feed: a variant with its description, keywords and choices, narrowed by this search. From a
      // search: a feed that is the search, and learns from what's marked in it.
      const body = feed
        ? { name, query: feedQuery, description: feed.description, keywords: feed.keywords, visibility: feed.visibility, show_sole_source: feed.show_sole_source }
        : { name, query: feedQuery };
      const r = await api<{ id: number }>("/api/feeds", { method: "POST", body });
      if (feed) {
        const sh = feed.shape;
        await api(`/api/feeds/${r.id}/shape`, { method: "PUT", body: { work: sh.work, path: sh.path, who: sh.who, named: !!sh.named, dow_only: !!sh.dow_only } });
      }
      await client.invalidateQueries({ queryKey: keys.feeds });
      navigate(`/feeds/${r.id}?view=all`);
      showToast(`Created ${name}.`);
    } catch (err) {
      showToast(errorText(err));
    }
  };

  useEffect(() => {
    if (feed) return;
    let current = true;
    const t = window.setTimeout(() => {
      api<SearchResponse>(`/api/search?q=${encodeURIComponent(query)}&limit=1`)
        .then((d) => {
          if (current) setCount(d.total);
        })
        .catch(() => undefined);
    }, 200);
    return () => {
      current = false;
      window.clearTimeout(t);
    };
  }, [query, feed]);

  const buyers = options.buyer ?? [];
  const text = (id: string, label: string, key: "all" | "phrase" | "any" | "none" | "sol" | "naics" | "psc" | "aln" | "won", hint: ReactNode = "", ph = "") => (
    <label className="field">
      <span>{label}</span>
      <input type="text" id={id} value={f[key]} placeholder={ph} onChange={(e) => set(key, e.target.value)} />
      {hint ? <small>{hint}</small> : null}
    </label>
  );
  const when = (n: "due" | "posted", label: string, opts: [string, string][]) => (
    <fieldset className="when">
      <legend>{label}</legend>
      <select id={`adv-${n}`} aria-label={label} value={f[n]} onChange={(e) => set(n, e.target.value)}>
        {opts.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
      <span className="range" id={`adv-${n}-range`} hidden={f[n] !== "range"}>
        <input type="date" id={`adv-${n}-from`} value={f[`${n}From`]} aria-label={`${label} from`} onChange={(e) => set(`${n}From`, e.target.value)} /> to{" "}
        <input type="date" id={`adv-${n}-to`} value={f[`${n}To`]} aria-label={`${label} to`} onChange={(e) => set(`${n}To`, e.target.value)} />
      </span>
    </fieldset>
  );
  const chosen = (name: string) => new Set(f.facets[name] ?? []);
  const setChosen = (name: string, values: Iterable<string>) => set("facets", { ...f.facets, [name]: [...values] });
  const picker = (name: string, opts: OptionValue[], placeholder: string, label: string, keep: (v: string) => boolean = () => false) => (
    <GroupPicker
      label={label}
      placeholder={placeholder}
      options={opts}
      chosen={new Set([...chosen(name)].filter((v) => !keep(v)))}
      onChange={(c) => setChosen(name, [...[...chosen(name)].filter(keep), ...c])}
      showCounts={false}
    />
  );
  const facet = (name: string) => {
    const opts = (options[name] ?? []).filter((o) => !o.parent || name !== "topic");
    if (name === "who") {
      // Open to anyone and small businesses as checks; vehicles and consortia each in their own box.
      const inBox = (prefix: string) => (v: string) => !v.startsWith(prefix);
      return (
        <>
          {checks(name, opts.filter((o) => !o.value.includes(":")))}
          <div className="adv-pickers">
            <div className="picker-field">
              <span>Orders under these contracts</span>
              {picker(name, opts.filter((o) => o.value.startsWith("vehicle:")), "Search vehicles: SeaPort, OASIS+, SEWP…", "Contract vehicles", inBox("vehicle:"))}
            </div>
            <div className="picker-field">
              <span>Calls through these consortia</span>
              {picker(name, opts.filter((o) => o.value.startsWith("consortium:")), "Search consortia: NSTIC, TReX, MTEC…", "Consortia", inBox("consortium:"))}
            </div>
          </div>
        </>
      );
    }
    const p = PICKED[name];
    return p ? picker(name, opts, p.placeholder, p.label) : checks(name, opts);
  };
  const checks = (name: string, opts: OptionValue[], anyLabel = "Any") => (
    <div className="checks">
      <label className="any">
        <input
          type="checkbox"
          checked={chosen(name).size === 0}
          onChange={() => setChosen(name, [])}
        />{" "}
        {anyLabel}
      </label>
      {opts.map((o) => {
          const on = (f.facets[name] ?? []).includes(o.value);
          return (
            <label key={o.value}>
              <input
                type="checkbox"
                name={name}
                value={o.value}
                checked={on}
                onChange={() =>
                  set("facets", {
                    ...f.facets,
                    [name]: on ? (f.facets[name] ?? []).filter((x) => x !== o.value) : [...(f.facets[name] ?? []), o.value],
                  })
                }
              />{" "}
              {o.label}
            </label>
          );
        })}
    </div>
  );

  return (
    <div className="advanced">
      <div className="date">{feed ? <Link to={`/feeds/${feed.id}`}>← Back to {feed.name}</Link> : <Link to={searchPath(q)}>← Back to results</Link>}</div>
      <h1>{feed ? `The search behind ${feed.name}` : "Advanced search"}</h1>
      {feed ? (
        <p className="adv-lede">
          Its description and keywords decide what comes into the feed; this search decides what stays. Records must match everything here. Leave a part empty
          and it doesn’t narrow anything.
        </p>
      ) : null}
      <form
        id="adv-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (feed) return void runPreview();
          remember(query);
          navigate(searchPath(query));
        }}
      >
        <section>
          <h2>Words</h2>
          <div className="adv-grid">
            {text("adv-all", "All of these words", "all", "In any order. Synonyms like UAS / unmanned / drone match too.", "counter-uas training")}
            {text("adv-phrase", "This exact phrase", "phrase", "", "live virtual constructive")}
            {text("adv-any", "Any of these words", "any", "", "autonomy ai")}
            {text("adv-none", "None of these words", "none", "", "janitorial construction")}
          </div>
          <fieldset className="inline-radios">
            <legend>Look in</legend>
            <label>
              <input type="radio" name="where" value="any" checked={f.where === "any"} onChange={() => set("where", "any")} /> Titles and descriptions
            </label>
            <label>
              <input type="radio" name="where" value="title" checked={f.where === "title"} onChange={() => set("where", "title")} /> Titles only
            </label>
          </fieldset>
        </section>
        <section>
          <h2>Numbers and codes</h2>
          <div className="adv-grid">
            {text("adv-sol", "Solicitation or notice number", "sol", "Any part from the start, e.g. W912 or HR0011-26.")}
            {text("adv-naics", "NAICS codes", "naics", "Prefixes allowed: 5417 covers every R&D industry.", "541715, 611430")}
            {text("adv-won", "Won by", "won", "Part of a company’s name; finds closed solicitations too.", "Leidos, Anduril")}
            {text("adv-psc", "Product and service codes (PSC)", "psc", "Prefixes allowed: AJ covers all R&D services.", "AJ, U")}
            {text("adv-aln", "Assistance listings", "aln", "Grants only.", "12.300")}
          </div>
        </section>
        <section>
          <h2>Buyer</h2>
          <BuyerPick id="adv-buyer" buyers={buyers} chosen={f.buyer} onChange={(v) => set("buyer", v)} placeholder="Type an agency, command or office…" label="Add a buyer" />
          <small>Choosing Navy includes NAVAIR, NAVSEA and every other Navy command. Any other text matches the listed agency name.</small>
          <div className="picker-field">
            <span>Leave out these buyers</span>
            <BuyerPick id="adv-not-buyer" buyers={buyers} chosen={f.notBuyer} onChange={(v) => set("notBuyer", v)} placeholder="Type a buyer to leave out…" label="Leave out a buyer" />
          </div>
        </section>
        <section>
          <h2>Dates</h2>
          <div className="adv-grid">
            {when("due", "Response due", [
              ["", "Any time"],
              ["7d", "In the next 7 days"],
              ["30d", "In the next 30 days"],
              ["90d", "In the next 90 days"],
              ["none", "No due date published"],
              ["range", "Between dates…"],
            ])}
            {when("posted", "Posted", [
              ["", "Any time"],
              ["1d", "In the last 24 hours"],
              ["7d", "In the last 7 days"],
              ["30d", "In the last 30 days"],
              ["range", "Between dates…"],
            ])}
          </div>
        </section>
        {ADV_FACETS.map(([n, l]) => (
          <section key={n}>
            <h2>{l}</h2>
            {facet(n)}
          </section>
        ))}
        <section hidden={!!feed}>
          <h2>Status</h2>
          <fieldset className="inline-radios">
            <legend className="sr">Status</legend>
            {(
              [
                ["open", "Open only"],
                ["any", "Open and closed"],
                ["closed", "Closed only"],
              ] as const
            ).map(([v, l]) => (
              <label key={v}>
                <input type="radio" name="status" value={v} checked={f.status === v} onChange={() => set("status", v)} /> {l}
              </label>
            ))}
          </fieldset>
          <label className="check">
            <input type="checkbox" id="adv-sole" checked={f.sole} onChange={(e) => set("sole", e.target.checked)} />
            <span>Include notices of intent to sole source</span>
          </label>
        </section>
        {f.nots.length ? (
          <section>
            <h2>Also in this search</h2>
            <p className="muted-s">
              Parts the form has no box for; they stay as they are: <code>{f.nots.join(" ")}</code>{" "}
              <button type="button" className="text-btn" id="adv-drop-nots" onClick={() => set("nots", [])}>
                Remove them
              </button>
            </p>
          </section>
        ) : null}
        {feed && preview ? <PreviewBox preview={preview} /> : null}
        <div className="adv-foot">
          <div className="adv-preview">
            <span>{feed ? "Feed search" : "Query"}</span>
            <code id="adv-query">{(feed ? feedQuery : query) || (feed ? "(nothing: the description and keywords decide)" : "(everything open)")}</code>
          </div>
          {feed ? (
            <>
              <button type="button" className="btn" id="adv-preview-btn" onClick={runPreview} disabled={preview === "loading"}>
                {preview === "loading" ? "Checking…" : "Preview changes"}
              </button>
              <button type="button" className="btn" id="adv-save-new" onClick={() => setNaming(true)}>
                Save as new feed…
              </button>
              <button type="button" className="btn primary" id="adv-save-feed" onClick={saveToFeed}>
                Save to {feed.name}
              </button>
            </>
          ) : (
            <>
              <button type="button" className="btn" id="adv-reset" onClick={() => (setF(parseForForm("")), navigate("/advanced"))}>
                Reset
              </button>
              <button type="button" className="btn" id="adv-save-new" onClick={() => setNaming(true)} disabled={!query.trim()}>
                Save as feed…
              </button>
              <button className="btn primary" id="adv-go">
                {count === null ? "Show results" : `Show ${count.toLocaleString()} result${count === 1 ? "" : "s"}`}
              </button>
            </>
          )}
        </div>
      </form>
      <NameDialog
        open={naming}
        onClose={() => setNaming(false)}
        title={feed ? "Save as a new feed" : "Save as a feed"}
        note={
          feed
            ? `A copy of ${feed.name} (its description, keywords and choices) narrowed by this search. ${feed.name} stays as it is.`
            : "The feed holds what this search finds, and learns from what you mark Pursue or Watch in it."
        }
        initial={feed ? `${feed.name} (narrowed)` : ""}
        onSave={(name) => {
          setNaming(false);
          void saveAsFeed(name);
        }}
      />
    </div>
  );
}

/** Buyers as removable chips, and a box that adds one: a known buyer from the list, or any agency text. */
function BuyerPick({ id, buyers, chosen, onChange, placeholder, label }: { id: string; buyers: OptionValue[]; chosen: string[]; onChange: (v: string[]) => void; placeholder: string; label: string }) {
  const [text, setText] = useState("");
  const labelFor = (v: string) => buyers.find((o) => o.value === v)?.label ?? v;
  const add = (t: string) => {
    t = t.trim();
    if (!t) return;
    const o = buyers.find((x) => x.label.toLowerCase() === t.toLowerCase());
    const v = o ? o.value : t.toLowerCase();
    if (!chosen.includes(v)) onChange([...chosen, v]);
    setText("");
  };
  return (
    <div className="buyer-pick">
      <div className="chips" id={`${id}s`}>
        {chosen.map((b, i) => (
          <button key={b} type="button" className="chip-f" data-i={i} onClick={() => onChange(chosen.filter((x) => x !== b))}>
            <span>{labelFor(b)}</span>
            <span aria-hidden="true">×</span>
            <span className="sr">Remove</span>
          </button>
        ))}
      </div>
      <input
        type="text"
        id={`${id}-input`}
        placeholder={placeholder}
        aria-label={label}
        list={`${id}-list`}
        value={text}
        onChange={(e) => {
          const v = e.target.value;
          setText(v);
          // Picking from the datalist fills the whole label at once.
          if (buyers.some((o) => o.label === v)) add(v);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            add(text);
          }
        }}
        onBlur={() => add(text)}
      />
      <datalist id={`${id}-list`}>
        {buyers.map((o) => (
          <option key={o.value} value={o.label} />
        ))}
      </datalist>
    </div>
  );
}

function PreviewBox({ preview }: { preview: Preview | "loading" }) {
  if (preview === "loading") return <p className="muted-s adv-changes">Working out what this search would keep…</p>;
  const views: ["open" | "upcoming" | "ending", string][] = [
    ["open", "Open"],
    ["upcoming", "Upcoming"],
    ["ending", "Contracts ending"],
  ];
  return (
    <section className="adv-changes" id="adv-changes" aria-live="polite">
      <h2>What this would change</h2>
      <p>
        {preview.added || preview.removed
          ? `Adds ${preview.added.toLocaleString()} record${preview.added === 1 ? "" : "s"} and takes away ${preview.removed.toLocaleString()}.`
          : "No change to what the feed holds."}
      </p>
      <dl className="adv-counts">
        {views.map(([k, l]) => (
          <div key={k}>
            <dt>{l}</dt>
            <dd>
              {preview.before[k].toLocaleString()} → <b>{preview.after[k].toLocaleString()}</b>
            </dd>
          </div>
        ))}
      </dl>
      <div className="adv-examples">
        {preview.remove_examples.length ? (
          <div>
            <h3>Would leave the feed</h3>
            <ul>
              {preview.remove_examples.map((x) => (
                <li key={x.key}>
                  <Link to={`/records/${x.key}`} target="_blank">
                    {x.title}
                  </Link>{" "}
                  <span className="muted-s">{x.buyer}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {preview.add_examples.length ? (
          <div>
            <h3>Would join it</h3>
            <ul>
              {preview.add_examples.map((x) => (
                <li key={x.key}>
                  <Link to={`/records/${x.key}`} target="_blank">
                    {x.title}
                  </Link>{" "}
                  <span className="muted-s">{x.buyer}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </section>
  );
}

/** Ask for a name, then hand it back. */
export function NameDialog({ open, onClose, title, note, initial, onSave }: { open: boolean; onClose: () => void; title: string; note: string; initial: string; onSave: (name: string) => void }) {
  return (
    <Modal open={open} onClose={onClose} id="name-dialog">
      <NameForm key={String(open)} onClose={onClose} title={title} note={note} initial={initial} onSave={onSave} />
    </Modal>
  );
}

function NameForm({ onClose, title, note, initial, onSave }: { onClose: () => void; title: string; note: string; initial: string; onSave: (name: string) => void }) {
  const [name, setName] = useState(initial);
  return (
    <form
      method="dialog"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim()) onSave(name.trim());
      }}
    >
      <div className="dlg">
        <h2>{title}</h2>
        <p>{note}</p>
        <label className="field">
          <span>Name</span>
          <input type="text" id="nd-name" maxLength={120} required autoFocus value={name} onFocus={(e) => e.target.select()} onChange={(e) => setName(e.target.value)} />
        </label>
      </div>
      <div className="foot">
        <button className="btn" type="button" onClick={onClose}>
          Cancel
        </button>
        <button className="btn primary">Create feed</button>
      </div>
    </form>
  );
}
